import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useCall } from '../../contexts/CallContext';

const MarkAsLeadModal = ({ open, onClose, assetContactMap, onSuccess }) => {
  const { markCallAsLead } = useCall();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize contacts when modal opens
  useEffect(() => {
    if (open && assetContactMap) {
      const contactGroups = {};
      
      Object.entries(assetContactMap).forEach(([assetId, contactNumber]) => {
        if (!contactGroups[contactNumber]) {
          contactGroups[contactNumber] = {
            contactNumber,
            assetIds: [],
            contactName: ''
          };
        }
        contactGroups[contactNumber].assetIds.push(assetId);
      });

      // Convert to array format for easier state management
      const contactsArray = Object.values(contactGroups);
      setContacts(contactsArray);
    }
  }, [open, assetContactMap]);

  const handleContactNameChange = (index, value) => {
    const updatedContacts = [...contacts];
    updatedContacts[index].contactName = value;
    setContacts(updatedContacts);
  };

  const handleRemoveContact = (index) => {
    const updatedContacts = contacts.filter((_, i) => i !== index);
    setContacts(updatedContacts);
  };

  const handleSubmit = async () => {
    if (contacts.length === 0) {
      setError('No contacts to process');
      return;
    }

    // Validate that at least one contact has a name
    const hasValidContact = contacts.some(contact => contact.contactName.trim());
    if (!hasValidContact) {
      setError('At least one contact must have a name');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const payload = {};
      contacts.forEach(contact => {
        if (contact.contactName.trim()) {
          contact.assetIds.forEach(assetId => {
            payload[assetId] = contact.contactName.trim();
          });
        }
      });

      const response = await markCallAsLead(payload);
      
      // Show success message
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      handleClose();
    } catch (err) {
      console.error('Error marking calls as leads:', err);
      setError(err.response?.data?.error || 'Failed to mark calls as leads');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setContacts([]);
    setError('');
    setLoading(false);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div">
          Convert Calls to Leads
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading && contacts.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography>Loading contacts...</Typography>
          </Box>
        ) : contacts.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography color="text.secondary">No contacts found</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {contacts.length === 1 
                ? `Add a name for this contact to create a new lead`
                : `Add names for the contacts you want to convert to leads`
              }
            </Typography>
            
            {contacts.map((contact, index) => (
              <Box 
                key={contact.contactNumber}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'background.paper'
                }}
              >
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500} color="primary">
                    {contact.contactNumber}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total calls: {contact.assetIds.length}
                  </Typography>
                </Box>
                
                <TextField
                  label="Contact Name"
                  value={contact.contactName}
                  onChange={(e) => handleContactNameChange(index, e.target.value)}
                  size="small"
                  placeholder="Enter contact name"
                  sx={{ minWidth: 200 }}
                />
                
                {contacts.length > 1 && (
                  <IconButton 
                    onClick={() => handleRemoveContact(index)}
                    color="error"
                    size="small"
                    title="Remove contact"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || contacts.length === 0 || !contacts.some(c => c.contactName.trim())}
          sx={{ minWidth: 100 }}
        >
          {loading ? 'Processing...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarkAsLeadModal; 